<template>
  <div>
    <div class="page-head">
      <div>
        <h2>系统参数</h2>
        <p>配置小程序内容、推荐官说明、登录参数和微信支付商户参数。</p>
      </div>
    </div>

    <el-card shadow="never" class="settings-card">
      <div slot="header" class="card-head">
        <span>微信小程序</span>
        <el-tag :type="miniProgramConfigStatus.complete ? 'success' : 'danger'">
          {{ miniProgramConfigStatus.complete ? '配置完整' : '配置不完整' }}
        </el-tag>
      </div>
      <el-form label-width="150px" :model="miniProgram">
        <el-form-item label="AppID"><el-input v-model="miniProgram.app_id" placeholder="小程序 AppID" /></el-form-item>
        <el-form-item label="AppSecret">
          <el-input v-model="miniProgram.app_secret" show-password :placeholder="secretPlaceholder(miniProgramConfigStatus.secret_summary, '小程序 AppSecret')" />
          <div class="field-help">服务端加密保存且不会返回浏览器；已有值留空即可保留。</div>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" class="settings-card">
      <div slot="header" class="card-head">
        <span>微信支付</span>
        <div>
          <el-button size="mini" type="success" :loading="savingPay" @click="savePayment">保存支付配置</el-button>
          <el-button size="mini" :loading="checkingPay" @click="checkPayment(false)">检查配置</el-button>
          <el-button size="mini" type="primary" :loading="checkingPay" @click="checkPayment(true)">测试连接</el-button>
        </div>
      </div>
      <el-alert
        :title="paymentStatusTitle"
        :type="paymentConfigStatus.complete ? (paymentConfigStatus.connected ? 'success' : 'warning') : 'error'"
        :closable="false"
        show-icon
        class="payment-alert"
      />
      <el-form label-width="150px" :model="wechatPay">
        <el-form-item label="商户号"><el-input v-model="wechatPay.mch_id" placeholder="微信支付商户号" /></el-form-item>
        <el-form-item label="APIv3 密钥"><el-input v-model="wechatPay.api_v3_key" show-password :placeholder="paymentSecretPlaceholder('api_v3_key', 'APIv3 Key')" /></el-form-item>
        <el-form-item label="商户证书序列号"><el-input v-model="wechatPay.merchant_serial_no" placeholder="Merchant Serial No" /></el-form-item>
        <el-form-item label="支付回调地址"><el-input v-model="wechatPay.notify_url" placeholder="https://example.com/api/miniprogram/payments/callback" /></el-form-item>
        <el-form-item label="商户私钥">
          <el-input v-model="wechatPay.private_key" type="textarea" :rows="6" :placeholder="paymentSecretPlaceholder('private_key', '-----BEGIN PRIVATE KEY-----')" />
          <div class="field-help">当前使用平台证书模式；保存后点击“测试连接”，系统会自动获取并维护微信支付平台证书，无需手工填写微信支付公钥。</div>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" class="settings-card">
      <div slot="header">关于我们</div>
      <el-form label-width="150px">
        <el-form-item label="栏目标签"><el-input v-model="aboutHero.label" /></el-form-item>
        <el-form-item label="主标题"><el-input v-model="aboutHero.title" /></el-form-item>
        <el-form-item label="简介"><el-input v-model="aboutHero.desc" type="textarea" :rows="3" /></el-form-item>
        <template v-for="(item, index) in aboutValues">
          <el-form-item :key="`value-title-${index}`" :label="`价值点 ${index + 1}`"><el-input v-model="item.title" placeholder="标题" /></el-form-item>
          <el-form-item :key="`value-desc-${index}`" label="说明"><el-input v-model="item.desc" placeholder="简短说明" /></el-form-item>
        </template>
        <el-form-item label="服务说明标题"><el-input v-model="aboutService.title" /></el-form-item>
        <el-form-item label="服务说明正文"><el-input v-model="aboutService.text" type="textarea" :rows="4" /></el-form-item>
        <el-form-item label="企业文化标题"><el-input v-model="aboutCulture.title" /></el-form-item>
        <el-form-item label="企业文化正文"><el-input v-model="aboutCulture.text" type="textarea" :rows="4" placeholder="一行一段，展示企业使命、愿景和价值观" /></el-form-item>
        <template v-for="(item, index) in aboutQualifications">
          <el-form-item :key="`qualification-title-${index}`" :label="`资质 ${index + 1}`"><el-input v-model="item.title" placeholder="资质名称" /></el-form-item>
          <el-form-item :key="`qualification-image-${index}`" label="资质示例图"><asset-upload v-model="item.image" category="qualification" /></el-form-item>
          <el-form-item :key="`qualification-desc-${index}`" label="资质说明"><el-input v-model="item.description" placeholder="编号、有效期或说明" /></el-form-item>
        </template>
      </el-form>
    </el-card>

    <el-card shadow="never" class="settings-card">
      <div slot="header">推荐官中心</div>
      <el-form label-width="150px">
        <el-form-item label="员工码"><el-input v-model="referralCenter.employeeCode" /></el-form-item>
        <el-form-item label="分享入口"><el-input v-model="referralCenter.shareLink" /></el-form-item>
        <el-form-item label="返佣方式"><el-radio-group v-model="referralCenter.commission_mode"><el-radio-button label="rate">按比例</el-radio-button><el-radio-button label="fixed">固定金额</el-radio-button></el-radio-group></el-form-item>
        <el-form-item v-if="referralCenter.commission_mode === 'rate'" label="成交返佣比例"><el-input-number v-model="referralCenter.commission_rate" :min="0" :max="100" :precision="2" /> <span class="field-help">%</span></el-form-item>
        <el-form-item v-else label="每单固定佣金"><el-input-number v-model="referralCenter.commission_fixed_amount" :min="0" :max="9999999" :precision="2" /> <span class="field-help">元/单</span></el-form-item>
        <el-form-item v-if="referralCenter.commission_mode === 'rate'" label="佣金计算基数"><el-select v-model="referralCenter.commission_basis"><el-option label="实际确认收入" value="confirmed_income"/><el-option label="小程序支付金额" value="order_amount"/><el-option label="旅游合同金额" value="contract_amount"/></el-select></el-form-item>
        <el-form-item label="可结算条件"><el-select v-model="referralCenter.settlement_trigger"><el-option label="订单完成后" value="order_completed"/><el-option label="支付成功后" value="paid"/></el-select></el-form-item>
        <el-form-item label="利润扣除佣金"><el-switch v-model="referralCenter.include_commission_in_cost" /></el-form-item>
        <el-form-item label="说明一"><el-input v-model="referralCenter.intro[0]" type="textarea" :rows="2" /></el-form-item>
        <el-form-item label="说明二"><el-input v-model="referralCenter.intro[1]" type="textarea" :rows="2" /></el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" class="settings-card">
      <div slot="header">小程序页面图片</div>
      <el-form label-width="150px" :model="miniProgramImages">
        <el-form-item label="推荐线路头图"><asset-upload v-model="miniProgramImages.featured_hero" category="mini-program" /></el-form-item>
        <el-form-item label="文化体验头图"><asset-upload v-model="miniProgramImages.culture_hero" category="mini-program" /></el-form-item>
        <el-form-item label="推荐官弹窗图"><asset-upload v-model="miniProgramImages.profile_referral" category="mini-program" /></el-form-item>
      </el-form>
    </el-card>

    <div class="settings-actions">
      <el-button type="primary" :loading="saving" @click="save">保存参数</el-button>
    </div>
  </div>
</template>

<script>
import { createResource, listResource, updateResource } from "../api/resources";
import AssetUpload from "../components/AssetUpload.vue";
import http from "../api/http";

const defaults = {
  wechat_mini_program: { app_id: "", app_secret: "" },
  wechat_pay: {
    mch_id: "",
    api_v3_key: "",
    merchant_serial_no: "",
    private_key: "",
    notify_url: ""
  },
  mini_program_images: {
    home_scenic_map: "/uploads/images/managed/home-scenic-map-beijing.png",
    featured_hero: "/uploads/images/managed/featured-hero.jpg",
    culture_hero: "/uploads/images/managed/culture-hero.jpg",
    scenic_map: "/uploads/images/managed/scenic-map-art.jpg",
    profile_referral: "/uploads/images/managed/profile-referral-gift-cut.png"
  },
  about_hero: { label: "惠享京程", title: "目的地定制游服务", desc: "" },
  about_values: [
    { title: "人工跟进", desc: "定制师对接需求" },
    { title: "目的地深度", desc: "文化路线与讲解" },
    { title: "灵活组合", desc: "酒店、车辆、体验" }
  ],
  about_service: { title: "服务说明", text: "" },
  about_culture: { title: "企业文化", text: "" },
  about_qualifications: [
    { title: "旅行社业务经营许可证", image: "", description: "" },
    { title: "营业执照", image: "", description: "" },
    { title: "服务资质", image: "", description: "" }
  ],
  referral_center: { employeeCode: "", shareLink: "", commission_mode: "rate", commission_rate: 0, commission_fixed_amount: 0, commission_basis: "confirmed_income", settlement_trigger: "order_completed", include_commission_in_cost: true, intro: ["", ""] }
};

function parseValue(value, fallback) {
  if (!value) return { ...fallback };
  if (typeof value === "object") return { ...fallback, ...value };
  try {
    return { ...fallback, ...JSON.parse(value) };
  } catch (error) {
    return { ...fallback };
  }
}

function parseArray(value, fallback) {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

export default {
  name: "SettingsManage",
  components: { AssetUpload },
  data() {
    return {
      saving: false,
      savingPay: false,
      checkingPay: false,
      paymentConfigStatus: { complete: false, missing: [], errors: [], connected: false },
      miniProgramConfigStatus: { complete: false, configured: {}, secret_summary: {} },
      settingRows: {},
      miniProgram: { ...defaults.wechat_mini_program },
      wechatPay: { ...defaults.wechat_pay },
      miniProgramImages: { ...defaults.mini_program_images },
      aboutHero: { ...defaults.about_hero },
      aboutValues: defaults.about_values.map((item) => ({ ...item })),
      aboutService: { ...defaults.about_service },
      aboutCulture: { ...defaults.about_culture },
      aboutQualifications: defaults.about_qualifications.map((item) => ({ ...item })),
      referralCenter: { ...defaults.referral_center, intro: [...defaults.referral_center.intro] }
    };
  },
  mounted() {
    this.fetchSettings();
    this.fetchMiniProgramStatus();
    this.fetchPaymentStatus();
  },
  computed: {
    paymentStatusTitle() {
      const status = this.paymentConfigStatus || {};
      if (!status.complete) {
        const details = (status.missing || []).concat(status.errors || []);
        return `支付配置不完整${details.length ? `：${details.join("、")}` : ""}`;
      }
      if (status.connected === true) return "支付配置完整，微信支付 API 连接及平台证书验签通过";
      return status.message || "支付必填项和密钥格式完整，尚未执行在线连接测试";
    }
  },
  methods: {
    async fetchSettings() {
      const res = await listResource("settings", { pageSize: 100 });
      const rows = res.data && res.data.items ? res.data.items : [];
      const map = rows.reduce((result, row) => {
        result[row.setting_key] = row;
        return result;
      }, {});
      this.settingRows = map;
      this.miniProgramImages = parseValue(map.mini_program_images && map.mini_program_images.setting_value, defaults.mini_program_images);
      this.aboutHero = parseValue(map.about_hero && map.about_hero.setting_value, defaults.about_hero);
      this.aboutService = parseValue(map.about_service && map.about_service.setting_value, defaults.about_service);
      this.aboutCulture = parseValue(map.about_culture && map.about_culture.setting_value, defaults.about_culture);
      const qualificationRaw = map.about_qualifications && map.about_qualifications.setting_value;
      const qualifications = parseArray(qualificationRaw, defaults.about_qualifications);
      this.aboutQualifications = defaults.about_qualifications.map((item, index) => ({ ...item, ...(qualifications[index] || {}) }));
      const valueRaw = map.about_values && map.about_values.setting_value;
      const values = parseArray(valueRaw, defaults.about_values);
      this.aboutValues = Array.isArray(values) && values.length ? values.slice(0, 3).map((item, index) => ({ ...defaults.about_values[index], ...item })) : defaults.about_values.map((item) => ({ ...item }));
      this.referralCenter = parseValue(map.referral_center && map.referral_center.setting_value, defaults.referral_center);
      this.referralCenter.commission_mode = this.referralCenter.commission_mode === "fixed" ? "fixed" : "rate";
      const storedRate = Number(this.referralCenter.commission_rate || 0);
      this.referralCenter.commission_rate = storedRate <= 1 ? storedRate * 100 : storedRate;
      this.referralCenter.commission_fixed_amount = Number(this.referralCenter.commission_fixed_amount || 0);
      this.referralCenter.intro = Array.isArray(this.referralCenter.intro) ? this.referralCenter.intro.slice(0, 2) : ["", ""];
      while (this.referralCenter.intro.length < 2) this.referralCenter.intro.push("");
    },
    async fetchMiniProgramStatus() {
      const res = await http.get("/wechat-mini-program/config-status", { showErrorMessage: false });
      this.miniProgramConfigStatus = res.data || this.miniProgramConfigStatus;
      this.miniProgram = {
        app_id: (this.miniProgramConfigStatus.values && this.miniProgramConfigStatus.values.app_id) || "",
        app_secret: ""
      };
    },
    async fetchPaymentStatus() {
      const res = await http.get("/wechat-pay/config-status", { showErrorMessage: false });
      this.paymentConfigStatus = res.data || this.paymentConfigStatus;
      const values = this.paymentConfigStatus.values || {};
      this.wechatPay = {
        ...defaults.wechat_pay,
        mch_id: values.mch_id || "",
        merchant_serial_no: values.merchant_serial_no || "",
        notify_url: values.notify_url || ""
      };
    },
    secretPlaceholder(summary, fallback) {
      return summary && summary.configured ? `${summary.summary}，留空保留` : fallback;
    },
    paymentSecretPlaceholder(key, fallback) {
      return this.secretPlaceholder((this.paymentConfigStatus.secret_summaries || {})[key], fallback);
    },
    validateWechatPay() {
      const required = [
        ["mch_id", "商户号"],
        ["api_v3_key", "APIv3 密钥"],
        ["merchant_serial_no", "商户证书序列号"],
        ["notify_url", "支付回调地址"],
        ["private_key", "商户私钥"]
      ];
      const configured = this.paymentConfigStatus.configured || {};
      const missing = required.filter(([key]) => (
        !String(this.wechatPay[key] || "").trim() && !configured[key]
      )).map(([, label]) => label);
      if (missing.length) throw new Error(`请完整填写微信支付配置：${missing.join("、")}`);
      if (this.wechatPay.api_v3_key && String(this.wechatPay.api_v3_key).trim().length !== 32) throw new Error("APIv3 密钥必须为32个字符");
      if (!/^https:\/\//i.test(this.wechatPay.notify_url)) throw new Error("支付回调地址必须使用 HTTPS");
    },
    async checkPayment(online) {
      this.checkingPay = true;
      try {
        const res = await http.post("/wechat-pay/check", { online });
        this.paymentConfigStatus = res.data || this.paymentConfigStatus;
        if (this.paymentConfigStatus.complete) this.$message.success(this.paymentConfigStatus.message || "支付配置检查通过");
      } finally {
        this.checkingPay = false;
      }
    },
    async savePayment() {
      try {
        this.validateWechatPay();
      } catch (error) {
        this.$message.error(error.message);
        return;
      }
      this.savingPay = true;
      try {
        const res = await http.put("/wechat-pay/config", this.wechatPay);
        this.paymentConfigStatus = res.data || this.paymentConfigStatus;
        this.$message.success("支付配置已保存并通过本地校验");
        await this.fetchPaymentStatus();
      } finally {
        this.savingPay = false;
      }
    },
    async upsertSetting(key, value, description, sortOrder) {
      const row = this.settingRows[key];
      const payload = {
        setting_key: key,
        setting_value: value,
        description,
        status: "active",
        sort_order: sortOrder
      };
      if (row && row.id) return updateResource("settings", row.id, payload);
      return createResource("settings", payload);
    },
    async save() {
      this.saving = true;
      try {
        await http.put("/wechat-mini-program/config", this.miniProgram);
        await Promise.all([
          this.upsertSetting("mini_program_images", this.miniProgramImages, "小程序页面图片配置", 100),
          this.upsertSetting("about_hero", this.aboutHero, "关于我们主内容", 1),
          this.upsertSetting("about_values", this.aboutValues, "关于我们价值点", 2),
          this.upsertSetting("about_service", this.aboutService, "关于我们服务说明", 3),
          this.upsertSetting("about_culture", this.aboutCulture, "关于我们企业文化", 4),
          this.upsertSetting("about_qualifications", this.aboutQualifications.filter((item) => item.title || item.image || item.description), "关于我们资质示例", 5),
          this.upsertSetting("referral_center", { ...this.referralCenter, commission_rate: Number(this.referralCenter.commission_rate || 0) / 100, commission_fixed_amount: Number(this.referralCenter.commission_fixed_amount || 0) }, "推荐官中心弹窗配置", 6)
        ]);
        this.$message.success("系统参数已保存");
        await Promise.all([this.fetchSettings(), this.fetchMiniProgramStatus()]);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.settings-card {
  margin-bottom: 18px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.payment-alert {
  margin-bottom: 18px;
}

.field-help {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
